import { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { local } from 'common/js/storage';

const PURCHASE_TRACKED_PREFIX = 'purchase_tracked';

const claimKey = (channel, reference) =>
  `${PURCHASE_TRACKED_PREFIX}_${channel}_${reference}`;

// A booking counts as purchased once QuickTravel has recorded at least one
// SUCCESSFUL payment against it. `payments_attributes[].success` is serialized
// on every payment and is the same field the analytics module already keys off,
// so it is available on every mount path of a confirmation view, including a
// revisit from the confirmation email.
//
// Deliberately NOT `balance_in_cents <= 0` (the shape the isFullyPaid hook
// uses): checkout supports deposit payments, so a genuinely paid booking can
// reach the confirmation view still carrying an outstanding balance. Gating on
// the balance would silently drop every deposit-paid conversion — a
// loss-direction failure, which is the direction this feature must never take
// (SBAU-611).
export function isBookingPaid(booking) {
  return Boolean(
    booking?.payments_attributes?.some((payment) => payment.success)
  );
}

// Purchase tracking is claimed once per channel per booking, so a remount, a
// reload, or a later revisit from the confirmation email does not re-fire it
// (SBAU-611).
//
// Channels are kept separate because the CCC confirm step renders both the
// Meta pixel effect and the Google Analytics effect on the same render — a
// single shared key would let whichever ran first silently suppress the other.
//
// The read and the write are deliberately SEPARATE functions rather than one
// test-and-set. A combined claim can only be taken before the send, and a claim
// taken before the send is burnt even when the send never happens — which loses
// the conversion permanently and irrecoverably. Splitting them lets every
// caller follow: verify preconditions -> send -> confirm the send happened ->
// then mark. The worst case then becomes a duplicate rather than a loss, and
// both channels carry a server-side dedupe backstop (Meta's eventID; GA4's
// transaction_id).
//
// Storage access goes through the common wrapper, which swallows errors. If
// storage is unavailable the read fails to null, so tracking still fires:
// over-counting is the deliberate failure direction.
export function isPurchaseTracked(channel, reference) {
  return (
    Boolean(reference) && Boolean(local.getItem(claimKey(channel, reference)))
  );
}

export function markPurchaseTracked(channel, reference) {
  if (!reference) {
    return;
  }

  local.setItem(claimKey(channel, reference), '1');
}

export function buildFacebookConversionData(booking) {
  // Prepare dataset for FB tracking
  const trackingData = {
    value: booking.gross_in_cents / 100,
    currency: booking.currency_iso_code,
    contents: [],
  };

  function addContent(reservation) {
    const existing = trackingData.contents.find(
      (item) => item.id === reservation.resource_id
    );

    if (!isEmpty(existing)) {
      existing.quantity += reservation.passenger_ids.length;
    } else {
      trackingData.contents.push({
        id: reservation.resource_id,
        name: reservation.selection_name,
        quantity: reservation.passenger_ids.length,
      });
    }
  }

  (booking.reservations_attributes || []).forEach((reservation) => {
    addContent(reservation);

    if (!isEmpty(reservation.sub_reservations_attributes)) {
      reservation.sub_reservations_attributes.forEach((subReservation) =>
        addContent(subReservation)
      );
    }
  });

  return trackingData;
}

// This hook assume that the the Vero tracking has been initialised elsewhere
export const useVeroTracking = (eventName) => {
  useEffect(() => {
    // eslint-disable-next-line
    const _veroq = window._veroq || [];

    _veroq.push(['track', eventName, {}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

// Meta pixel purchase tracking for a confirmation view. Shared by both tenants
// rather than copy-pasted, so the ordering rule below has exactly one home
// (SBAU-611).
//
// Order is load-bearing: verify preconditions -> fire -> then mark. `fbq` is
// checked first because the pixel script is blocked by a large share of
// browsers; firing into a missing global and marking anyway would burn the
// claim without ever sending the event. Marking last also means a throw inside
// buildFacebookConversionData leaves the claim untaken, so a later view can
// still fire correctly.
//
// The paid gate matters because a confirmation route is reachable before
// payment. An unpaid visit must fire nothing AND mark nothing — marking on an
// unpaid visit would suppress the real conversion once the customer pays.
export const useMetaPurchaseTracking = (booking) => {
  const reference = booking?.reference;
  const paid = isBookingPaid(booking);

  useEffect(() => {
    if (!reference || !paid) {
      return;
    }

    // eslint-disable-next-line no-undef
    if (typeof fbq === 'undefined' || !fbq) {
      return;
    }

    if (isPurchaseTracked('fb', reference)) {
      return;
    }

    // eslint-disable-next-line no-undef
    fbq('track', 'Purchase', buildFacebookConversionData(booking), {
      eventID: reference,
    });

    markPurchaseTracked('fb', reference);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, paid]);
};
